import React, { ReactElement, useEffect, useState } from 'react'
import PageHeader from '../components/PageHeader'
import Table from '../components/Table'
import Radom, { Deposit, DepositAccount, DepositAccountType, DepositCryptoAddressType } from '../state/Radom'
import { observer } from 'mobx-react'
import { IconButton, PrimaryButton } from '../components/Button'
import { errorToast, shortAddress, roundAssetAmount, successToast, useTestMode } from '../util/Util'
import { Container } from '../components/Animations'
import Pagination from '../components/Pagination'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Checkbox from '../components/Checkbox'
import Modal from '../components/Modal'
import { RADOM_COLORS } from '../util/Constants'
import CopyButton from '../components/CopyButton'
import CreateDepositAddressModal from '../components/CreateDepositAddressModal'
import { Link } from 'react-router-dom'
import Tabs from '../components/Tabs'
import { getMethod } from '../util/Managed'
import { Chevron } from '../icons/Chevron'
import TimeText from '../components/TimeText'
import LinkExternal from '../components/LinkExternal'
import { ExternalLinkText } from './Events'
import PaymentMethodDisplay from '../components/PaymentMethodDisplay'
import NewDots from '../icons/NewDots'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import { formatDepositAccount, formatDepositAccountType } from '../components/Shared'
import { TextInput } from '../components/Input'
import Close from '../icons/Close'

const DepositAddresses = observer((): ReactElement => {
  const pageSize = 10
  const [testMode] = useTestMode()
  const [isLoading, setIsLoading] = useState(false)
  const [curPage, setCurPage] = useState(0)
  const [exportDepositAddresses, setExportDepositAddresses] = useState(false)
  const [isCreateDepositAddressModalOpen, setIsCreateDepositAddressModalOpen] = useState(false)
  const [depositAddresses, setDepositAddresses] = useState<DepositAccount[]>([])
  const [totalDepositAddressesSize, setTotalDepositAddressesSize] = useState(0)
  const [accountTypes, setAccountTypes] = useState<DepositCryptoAddressType[]>([
    DepositCryptoAddressType.FirstParty, DepositCryptoAddressType.ThirdParty
  ])
  const [showDetailsPanel, setShowDetailsPanel] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<DepositAccount | null>(null)
  const [selectedTab, setSelectedTab] = useState<string>('Details')
  const [deposits, setDeposits] = useState<Deposit[]>([])
  const [isLoadingDeposits, setIsLoadingDeposits] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPrivateTreasuryAddress, setSelectedPrivateTreasuryAddress] = useState<string | undefined>()

  const fetchDepositAddresses = async (): Promise<void> => {
    setIsLoading(true)
    setDepositAddresses([])
    setTotalDepositAddressesSize(0)
    const isExternal = accountTypes.length === 2 || accountTypes.length === 0
      ? undefined
      : accountTypes[0] === DepositCryptoAddressType.ThirdParty
    const isSearching = searchQuery.trim().length > 0
    const offset = isSearching ? 0 : curPage * pageSize
    const limit = isSearching ? 250 : pageSize

    Radom.listDepositAccounts(testMode, offset, limit, isExternal, [DepositAccountType.Crypto])
      .then(res => {
        setDepositAddresses(res.data)
        setTotalDepositAddressesSize(res.totalCount)
      })
      .finally(() => setIsLoading(false)
      )
  }

  const fetchDeposits = async (id: string): Promise<void> => {
    setIsLoadingDeposits(true)
    setDeposits([])

    Radom.listDepositsByAccountId(id)
      .then(res => {
        setDeposits(res)
      })
      .finally(() => setIsLoadingDeposits(false)
      )
  }

  useEffect(() => {
    fetchDepositAddresses()
  }, [testMode, curPage, accountTypes, searchQuery])

  useEffect(() => {
    setCurPage(0)
  }, [searchQuery, accountTypes, testMode])

  useEffect(() => {
    const treasuryId = selectedAccount?.accountDetails.Crypto?.treasuryId
    setSelectedPrivateTreasuryAddress(undefined)
    if (!treasuryId) {
      return
    }

    let isMounted = true
    Radom.getPrivateTreasury()
      .then((treasury) => {
        if (isMounted && treasury?.id === treasuryId) {
          setSelectedPrivateTreasuryAddress(treasury.evmAddress)
        }
      })
      .catch(() => undefined)

    return () => {
      isMounted = false
    }
  }, [selectedAccount?.accountDetails.Crypto?.treasuryId])

  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  const filteredDepositAddresses = depositAddresses.filter(account => {
    if (!normalizedSearchQuery) {
      return true
    }

    const terms = [
      account.id,
      account.name,
      account.accountDetails.Crypto?.address,
      account.customer?.name,
      account.customer?.email,
      account.customer?.id
    ]

    return terms.some(term => term?.toLowerCase().includes(normalizedSearchQuery))
  })
  const visibleDepositAddresses = normalizedSearchQuery
    ? filteredDepositAddresses.slice(curPage * pageSize, (curPage + 1) * pageSize)
    : filteredDepositAddresses
  const visibleDepositAddressCount = normalizedSearchQuery ? filteredDepositAddresses.length : totalDepositAddressesSize
  return <Container>
    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <PageHeader title='Deposit addresses' />
          <div style={{ display: 'flex', flexDirection: 'row', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ minWidth: 260, flex: '1 1 260px' }}>
              <TextInput
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder='Search by address, name, customer, or ID'
              />
            </div>
            <Dropdown
              selectedContent={<div style={{ fontSize: 14 }}>{'Account Type'}</div>}
              dropdownContent={<div>
                {Object.values(DepositCryptoAddressType).map(s =>
                  <DropdownItem
                    key={s}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 14 }}
                    onClick={() => {
                      setAccountTypes((prev) =>
                        prev.includes(s)
                          ? prev.filter((x) => x !== s)
                          : [...prev, s])
                    }}>
                    <Checkbox size={16} checked={accountTypes.includes(s)} />
                    <span>{s}</span>
                  </DropdownItem>
                )}
              </div>} />
          </div>
          {normalizedSearchQuery && <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 13 }}>
            Searching across the most recent 250 deposit addresses.
          </span>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', rowGap: 20, justifyContent: 'flex-end' }}>
          <PrimaryButton style={{ fontSize: 14 }} onClick={() => {
            setIsCreateDepositAddressModalOpen(true)
          }}>
            <span>Create address</span>
          </PrimaryButton>
        </div>
      </div>

      <Table
        onClickRow={(rowIndex: number) => {
          const d = visibleDepositAddresses[rowIndex]
          setSelectedTab('Details')
          setSelectedAccount(d)
          setShowDetailsPanel(true)
          if (deposits.length === 0 ||
              deposits[0].depositAccountId !==
              d.id) { fetchDeposits(d.id) }
        }}
        borderless
        headers={['Address name', 'Related entity', 'Deposit address', 'Supported assets'].map(h => <span key={h}>{h}</span>)}
        items={
          visibleDepositAddresses.map((d) => [
            <div key={0} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: RADOM_COLORS.BLACK, fontWeight: 500 }}>{d.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ opacity: 0.58, fontSize: 13, fontFamily: 'var(--font-mono)' }}>{shortAddress(d.id, 6)}</span>
                <CopyButton text={d.id} />
              </div>
            </div>,
            <div key={2}>
              {d?.accountDetails.Crypto?.isExternal && d.customer
                ? <Link to={`/customer/${d.customer.id}`} style={{ color: RADOM_COLORS.BLACK }}>
                  {d.customer.name.length
                    ? d.customer.name
                    : d.customer.email}
                </Link>
                : 'Own address'}
            </div>,
            <div key={3} style={{ display: 'flex', gap: 4 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5, color: RADOM_COLORS.BLACK }}>
                {formatDepositAccount(d.accountDetails)}
              </span>
              {d.accountDetails.Crypto && <CopyButton text={d.accountDetails.Crypto?.address ?? ''} />}
            </div>,
            <div key={4}>
              {formatDepositAccountType(d.accountDetails)}
            </div>
          ])
        }
        isLoading={isLoading}
        emptyPlaceholderText={normalizedSearchQuery ? 'No deposit addresses match this search' : 'No deposit addresses yet'}
      />

      {showDetailsPanel && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(17, 17, 17, 0.28)',
            zIndex: 99
          }}
          onClick={() => setShowDetailsPanel(false)}
        />
      )}

      <div
        style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: 'min(100%, 560px)',
            height: '100%',
            background: 'white',
            boxSizing: 'border-box',
            boxShadow: '-18px 0 48px rgba(15, 23, 42, 0.16)',
            transform: showDetailsPanel ? 'none' : 'translateX(150%)',
            transition: 'transform 0.3s ease',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            overflow: 'hidden',
            borderLeft: '1px solid #eceff3'
          }}
      >

        <div
          style={{
            fontSize: 14,
            width: '100%',
            height: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: 24,
            gap: 16,
            overflowY: 'auto'
          }}
        >
          <div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 12.5 }}>Deposit address</span>
              <span style={{ fontSize: 20, fontWeight: 500, color: RADOM_COLORS.BLACK, wordBreak: 'break-word' }}>{selectedAccount?.name}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Dropdown
                noChevron
                selectedContent={<NewDots width={20}/>}
                dropdownContent={
                  <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                    <ConfirmButtonModal
                      title='Archive address'
                      description='Are you sure you want to archive this deposit address?'
                      action='Archive address'
                      onConfirm={async () => {
                        if (!selectedAccount) return
                        setShowDetailsPanel(false)
                        Radom.deleteDepositAccount(selectedAccount.id)
                          .then(async () => await fetchDepositAddresses())
                          .then(() => {
                            successToast('Deposit address archived')
                          })
                      }}
                      ActionComponent={(props: any) => <DropdownItem {...props}>Archive address</DropdownItem>}
                    />
                  </div>
                }
              />
              <IconButton type='button' onClick={() => setShowDetailsPanel(false)} style={{ padding: 6 }}>
                <Close width={16} height={16} />
              </IconButton>
            </div>
          </div>
          <div
            style={{
              marginTop: 4,
              marginBottom: 8
            }}
          >
            <Tabs
              selectedTab={selectedTab}
              tabs={['Details', 'Deposits']}
              onTabChange={(tab) => { setSelectedTab(tab) }} />
          </div>

          {selectedTab === 'Details'
            ? <>
              {selectedAccount?.customer && <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <span style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>Related Beneficiary</span>
                <div style={{ padding: '11px 12px', borderRadius: 12, border: '1px solid #eceff3', background: '#f9fafb' }}>
                  <Link to={`/customer/${selectedAccount.customer.id}`} style={{ color: RADOM_COLORS.BLACK }}>
                    {selectedAccount.customer.name.length
                      ? selectedAccount.customer.name
                      : selectedAccount.customer.email}
                  </Link>
                </div>
              </div>}

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <span style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>Deposit Address ID</span>
                <div style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '11px 12px',
                  borderRadius: 12,
                  border: '1px solid #eceff3',
                  background: '#f9fafb'
                }}>
                  <span style={{ color: RADOM_COLORS.BLACK, fontFamily: 'var(--font-mono)', fontSize: 13, wordBreak: 'break-all' }}>
                    {selectedAccount?.id}
                  </span>
                  <CopyButton text={selectedAccount?.id ?? ''} />
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <span style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>Deposit Address</span>
                <div style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '11px 12px',
                  borderRadius: 12,
                  border: '1px solid #eceff3',
                  background: '#f9fafb'
                }}>
                  <span style={{ color: RADOM_COLORS.BLACK, fontFamily: 'var(--font-mono)', fontSize: 13, wordBreak: 'break-all' }}>
                    {selectedAccount?.accountDetails.Crypto?.address}
                  </span>
                  <CopyButton text={selectedAccount?.accountDetails.Crypto?.address ?? ''} />
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <span style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>Treasury</span>
                <div style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '11px 12px',
                  borderRadius: 12,
                  border: '1px solid #eceff3',
                  background: '#f9fafb'
                }}>
                  <span style={{ color: RADOM_COLORS.BLACK, fontSize: 13 }}>
                    {selectedAccount?.accountDetails.Crypto?.treasuryId ? 'Private treasury' : 'Radom treasury'}
                  </span>
                  {selectedAccount?.accountDetails.Crypto?.treasuryId && selectedPrivateTreasuryAddress && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                      {shortAddress(selectedPrivateTreasuryAddress, 6)}
                    </span>
                    <CopyButton text={selectedPrivateTreasuryAddress} />
                  </div>}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <span style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>Supported Assets</span>
                {selectedAccount?.accountDetails.Crypto && <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {
                    selectedAccount.accountDetails.Crypto?.depositMethods.map(m =>
                      <PaymentMethodDisplay
                        key={m.network + (m.token ?? '')}
                        method={{ network: m.network, token: m.token }}
                        excludeName
                        disableTooltip={false} />
                    )
                  }
                </div>}
              </div>
            </>
            : <>

              <Table
                borderless
                disableOnClickState
                headers={['Deposit ID', 'Summary', 'Date'].map(h => <span key={h}>{h}</span>)}
                isLoading={isLoadingDeposits}
                items={deposits.map(d => {
                  let summary: {
                    in: {
                      img: string
                      amount: number | string
                      ticker: string
                    }
                    out?: {
                      img: string
                      amount: number | string
                      ticker: string
                    }
                  }

                  if (d.depositMethod.Crypto) {
                    const method = getMethod(d.depositMethod.Crypto.network, d.depositMethod.Crypto.token)
                    summary = {
                      in: {
                        img: method.logo,
                        amount: d.netAmount,
                        ticker: method.ticker
                      }
                    }
                  } else {
                    const outMethod = getMethod(d.depositMethod.Fiat?.onrampNetwork || '', d.depositMethod.Fiat?.onrampToken)
                    const inMethod = getMethod('Fiat', d.depositMethod.Fiat?.fiatCurrency)

                    summary = {
                      in: {
                        img: inMethod.logo,
                        amount: d.fiatGrossAmount || '',
                        ticker: inMethod.ticker
                      },
                      out: {
                        img: outMethod.logo,
                        amount: d.netAmount,
                        ticker: outMethod.ticker
                      }
                    }
                  }
                  return [
                    <div key={'1'}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <ExternalLinkText
                          onClick={e => e.stopPropagation()}
                          target="_blank"
                          href={`/deposit/${d.id}?source=transactions`}>
                          {shortAddress(d.id, 3)}
                          <LinkExternal stroke={RADOM_COLORS.BLACK} width={14} />
                        </ExternalLinkText>
                        <CopyButton text={d.id} />
                      </div>
                    </div>,
                    <div key={'2'} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <img src={summary.in.img} style={{ width: 20, height: 20 }} />
                      <span>
                        {roundAssetAmount(summary.in.ticker, Number(summary.in.amount))}
                      </span>
                      <span>{summary.in.ticker}</span>
                      {
                        summary.out && <>
                          <Chevron style={{ opacity: 0.65, transform: 'rotate(-90deg)' }} />
                          <img src={summary.out.img} style={{ width: 20, height: 20 }} />
                          <span>
                            {roundAssetAmount(summary.out.ticker, Number(summary.out.amount))}
                          </span>
                          <span>{summary.out.ticker}</span>
                        </>
                      }
                    </div>,
                    <div key={'3'}>
                      <TimeText date={new Date(d.depositedAt)} />
                    </div>
                  ]
                })}
                emptyPlaceholderText='No deposits yet'
              />
            </>}
        </div>
      </div>

      {
        !isLoading && <Pagination
        curPage={curPage}
          numPages={Math.max(1, Math.ceil(visibleDepositAddressCount / pageSize))}
          onPageClick={page => setCurPage(page)}
        />
      }

    </div>

    {
      exportDepositAddresses &&
      <Modal visible={exportDepositAddresses} onClose={() => setExportDepositAddresses(false)} title='Export deposit addresses'>
        <></>
      </Modal>
    }

    {
      isCreateDepositAddressModalOpen &&
        <CreateDepositAddressModal
          title='Create deposit address'
          visible={isCreateDepositAddressModalOpen}
          onClose={() => setIsCreateDepositAddressModalOpen(false)}
          onAccountCreated={() => {
            setIsCreateDepositAddressModalOpen(false)
            fetchDepositAddresses()
          }} />
    }
  </Container>
})

export default DepositAddresses
